import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Hairline,
  StatusPill,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import { useApplicationsBadge } from "../lib/use-applications-badge.js";
import { ApproveApplicationModal } from "../components/ApproveApplicationModal.js";
import { RejectApplicationModal } from "../components/RejectApplicationModal.js";
import type { AdminApplicationDetail, ApplicationStatus } from "@wmart/shared-types";

const STATUS_TONE: Record<ApplicationStatus, Tone> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
        {label}
      </dt>
      <dd className="mt-1 text-ink-900">{value ?? "—"}</dd>
    </div>
  );
}

export function ApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();
  const { refresh: refreshBadge } = useApplicationsBadge();
  const [application, setApplication] = useState<AdminApplicationDetail | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<AdminApplicationDetail>(
        `/v1/admin/applications/${id}`,
      );
      setApplication(data);
      setLoadError(false);
    } catch (err) {
      setLoadError(true);
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load application");
    }
  }, [id, notify, notifyProblem]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleApproved() {
    setShowApproveModal(false);
    notify("success", "Application approved");
    await refreshBadge();
    void navigate("/applications");
  }

  async function handleRejected() {
    setShowRejectModal(false);
    notify("success", "Application rejected");
    await refreshBadge();
    void navigate("/applications");
  }

  if (loadError && !application) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/applications" className="text-body-sm text-accent hover:underline">
          ← Back to applications
        </Link>
        <p className="text-body-sm text-danger">Application could not be loaded.</p>
      </div>
    );
  }

  if (!application) {
    return <p className="text-body-sm text-ink-500">Loading…</p>;
  }

  const isPending = application.status === "pending";

  return (
    <div className="flex flex-col gap-6">
      <Link to="/applications" className="text-body-sm text-accent hover:underline">
        ← Back to applications
      </Link>

      {/* Status banner */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <StatusPill tone={STATUS_TONE[application.status]}>
              {isPending ? "Pending review" : application.status}
            </StatusPill>
            {application.status === "approved" && application.reviewed_at ? (
              <p className="text-body-sm text-ink-500">
                Approved on {formatDate(application.reviewed_at)}
              </p>
            ) : null}
          </div>

          {isPending ? (
            <div className="flex gap-2">
              <Button onClick={() => setShowApproveModal(true)}>
                Approve
              </Button>
              <Button variant="danger" onClick={() => setShowRejectModal(true)}>
                Reject
              </Button>
            </div>
          ) : null}
        </div>

        {application.status === "rejected" ? (
          <div className="mt-4 rounded-sm border border-danger bg-danger-soft px-4 py-3 text-body-sm text-danger">
            <p className="font-medium">Rejection reason</p>
            <p className="mt-1">
              {application.rejection_reason ?? "(no reason recorded)"}
            </p>
            <p className="mt-2 text-ink-500">
              The applicant may resubmit with corrected information.
            </p>
          </div>
        ) : null}
      </Card>

      {/* Contact section */}
      <Card className="p-6">
        <h3 className="font-serif text-title-md text-ink-900">Contact</h3>
        <Hairline spacing="sm" />
        <dl className="grid grid-cols-1 gap-3 text-body-sm sm:grid-cols-2">
          <Field label="Email" value={application.user_email} />
          <Field label="Full name" value={application.contact_full_name} />
          <Field label="Phone" value={application.contact_phone} />
          <Field label="Submitted" value={formatDate(application.created_at)} />
        </dl>
      </Card>

      {/* Business section */}
      <Card className="p-6">
        <h3 className="font-serif text-title-md text-ink-900">Business</h3>
        <Hairline spacing="sm" />
        <dl className="grid grid-cols-1 gap-3 text-body-sm sm:grid-cols-2">
          <Field label="Legal name" value={application.business_legal_name} />
          <Field label="Business type" value={application.business_type} />
          <Field label="VAT number" value={application.vat_number} />
        </dl>
      </Card>

      {/* Trading address section */}
      <Card className="p-6">
        <h3 className="font-serif text-title-md text-ink-900">Trading address</h3>
        <Hairline spacing="sm" />
        <dl className="grid grid-cols-1 gap-3 text-body-sm sm:grid-cols-2">
          <Field label="Address line 1" value={application.address_line_1} />
          <Field label="Address line 2" value={application.address_line_2} />
          <Field label="City" value={application.address_city} />
          <Field label="Postcode" value={application.address_postcode} />
          <Field label="Country" value={application.address_country} />
        </dl>
      </Card>

      {showApproveModal ? (
        <ApproveApplicationModal
          applicationId={application.id}
          onApproved={handleApproved}
          onClose={() => setShowApproveModal(false)}
        />
      ) : null}

      {showRejectModal ? (
        <RejectApplicationModal
          applicationId={application.id}
          onRejected={handleRejected}
          onClose={() => setShowRejectModal(false)}
        />
      ) : null}
    </div>
  );
}
